// src/components/padrekko/qc/FindingNotesSection.jsx
//
// Day 8 Phase A — per-finding notes. Mounted inside QcFindingCard's
// expanded body, BELOW the regulatory analysis sections (sev/conf row +
// chipActions). Distinct from DisputedBanner which stays at the top.
//
// Rendering modes:
//   - count === 0 AND compose inactive: only the "+ Add note" trigger renders
//   - count === 0 AND compose active:   trigger hidden, compose box visible
//   - count > 0:                        notes list + compose box always visible
//
// Sort: chronological ascending (oldest first). Compose is at the bottom
// so the user types right after the most recent reply.
//
// Author display: backend joins author_display via COALESCE; we fall back
// to "Former member" for orphaned user_ids. No author_username: it was an
// email address, and the server no longer returns it (REM-426).

import { useState, useRef, useImperativeHandle, forwardRef } from "react";
import { MessageSquare, Loader2, Pencil, Trash2, X as XIcon, AlertCircle, Check } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { useAuthStore } from "../../../stores/useAuthStore";
import { useEffectiveOrg } from "../../../hooks/useEffectiveOrg";

const NOTE_SOFT_MAX = 2000;

function formatRelativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

function authorLabel(note) {
  const name = note.author_display ?? "Former member";
  // Server-resolved: snapshotted at write time, falling back to a join
  // for notes written before migration 042.
  const org = note.author_org_display || "Unknown org";
  return `${name} · ${org}`;
}

/* ── Single note row ─────────────────────────────────────────────────── */
function NoteRow({ note, currentUserId, isServiceProvider, onEdit, onDelete, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [error, setError] = useState(null);
  const editInflightId = useQcStore((s) => s.editNoteInflightId);
  const deleteInflightId = useQcStore((s) => s.deleteNoteInflightId);
  const isSavingEdit = editInflightId === note.id;
  const isDeleting = deleteInflightId === note.id;

  const isAuthor = note.author_user_id === currentUserId;
  // readOnly (case complete) locks edit/delete to match the backend 403.
  const canMutate = (isAuthor || isServiceProvider) && !readOnly;

  // `edited` is SERVER-DERIVED, not counted from edit_history. The drafts are
  // withheld from other orgs (the author's working-out), and computing this
  // from an array that is deliberately absent reported every edited note as
  // never edited — an absence turned into a false statement about the record.
  const wasEdited = note.edited === true;

  async function handleSaveEdit() {
    setError(null);
    const result = await onEdit(note.id, draft);
    if (result.ok) {
      setEditing(false);
    } else {
      setError(result.error || "Failed to save");
    }
  }
  async function handleDelete() {
    setError(null);
    const result = await onDelete(note.id, note.case_id);
    if (!result.ok) {
      setError(result.error || "Failed to delete");
    }
  }

  return (
    <div className="px-2 py-1.5 rounded border border-slate-800/60 bg-slate-900/30">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] text-slate-400 leading-snug min-w-0">
          <span className="text-slate-200 font-medium">{authorLabel(note)}</span>
          <span className="text-fg-secondary mx-1">·</span>
          <span className="text-fg-secondary" title={note.created_at}>
            {formatRelativeTime(note.created_at)}
          </span>
          {wasEdited && (
            <>
              <span className="text-fg-secondary mx-1">·</span>
              <span className="text-fg-secondary italic" title={`Edited ${formatRelativeTime(note.updated_at)}`}>
                edited
              </span>
            </>
          )}
        </div>
        {canMutate && !editing && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => { setDraft(note.body); setEditing(true); setError(null); }}
              disabled={isSavingEdit || isDeleting}
              className="p-0.5 rounded text-fg-secondary hover:text-slate-200 transition
                         disabled:opacity-40 disabled:cursor-not-allowed"
              title="Edit"
            >
              <Pencil size={10} />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isSavingEdit || isDeleting}
              className="p-0.5 rounded text-fg-secondary hover:text-red-300 transition
                         disabled:opacity-40 disabled:cursor-not-allowed"
              title="Delete"
            >
              {isDeleting
                ? <Loader2 size={10} className="animate-spin" />
                : <Trash2 size={10} />}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="text-[11px] text-slate-100 leading-relaxed mt-1 whitespace-pre-wrap break-words">
          {note.body}
        </div>
      ) : (
        <div className="mt-1 space-y-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={NOTE_SOFT_MAX}
            rows={3}
            disabled={isSavingEdit}
            autoFocus
            className="w-full text-[11px] px-2 py-1.5 rounded border
                       border-slate-700 bg-slate-900 text-slate-100
                       focus:outline-none focus:border-slate-500
                       disabled:opacity-50 leading-snug resize-none"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[9px] text-fg-secondary tabular-nums">
              {draft.length}/{NOTE_SOFT_MAX}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => { setEditing(false); setError(null); }}
                disabled={isSavingEdit}
                className="text-[10px] px-2 py-0.5 rounded border
                           border-slate-700 text-slate-400 hover:bg-slate-800
                           disabled:opacity-40 transition"
              >
                <XIcon size={9} className="inline" /> Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={isSavingEdit || !draft.trim() || draft === note.body}
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border
                           bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                           hover:bg-[#16f2d4]/25
                           disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {isSavingEdit
                  ? <Loader2 size={9} className="animate-spin" />
                  : <Check size={9} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-1.5 text-[10px] text-red-300 leading-snug mt-1">
          <AlertCircle size={10} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/* ── Section ─────────────────────────────────────────────────────────── */
const FindingNotesSection = forwardRef(function FindingNotesSection(
  { finding, caseId },
  ref
) {
  const notesByCase = useQcStore((s) => s.notesByCase);
  const addFindingNote = useQcStore((s) => s.addFindingNote);
  const editNote = useQcStore((s) => s.editNote);
  const deleteNote = useQcStore((s) => s.deleteNote);
  const findingNoteInflightFindingId = useQcStore((s) => s.findingNoteInflightFindingId);
  const isSubmitting = findingNoteInflightFindingId === finding.id;
  // Phase 6 — close lockdown: read-only notes at 'complete' (no add/edit/delete).
  const caseComplete = useQcStore((s) => s.caseInfo?.current_stage === "complete");

  const user = useAuthStore((s) => s.user);
  const currentUserId = user?.id ?? null;
  const effectiveOrg = useEffectiveOrg();
  const isServiceProvider = effectiveOrg?.is_service_provider === true;

  const notes = notesByCase.get(caseId)?.findings.get(finding.id) ?? [];
  const count = notes.length;

  const [composeOpen, setComposeOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [inlineError, setInlineError] = useState(null);

  const rootRef = useRef(null);
  useImperativeHandle(ref, () => ({
    scrollIntoView() {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
  }));

  async function handleSubmit() {
    setInlineError(null);
    const result = await addFindingNote(finding.id, caseId, draft);
    if (result.ok) {
      setDraft("");
      setComposeOpen(count === 0 ? false : true);
    } else {
      setInlineError(result.error || "Failed to add note");
    }
  }

  // The section lives inside QcFindingCard's root onClick={handleCardClick}.
  // Stop propagation at this level so internal clicks (Add note trigger,
  // submit, edit/delete, textarea focus) don't bubble up and collapse
  // the card. mousedown is the actual culprit for textarea focus on some
  // browsers; covering both is belt+suspenders.
  const stop = (e) => e.stopPropagation();

  // Nothing to show + compose closed → render the trigger only. At 'complete'
  // there's nothing to add, so render nothing.
  if (count === 0 && !composeOpen) {
    if (caseComplete) return null;
    return (
      <div
        ref={rootRef}
        className="pt-1"
        onClick={stop}
        onMouseDown={stop}
      >
        <button
          type="button"
          onClick={(e) => { stop(e); setComposeOpen(true); }}
          className="flex items-center gap-1 text-[10px] text-fg-secondary
                     hover:text-slate-200 transition"
        >
          <MessageSquare size={10} />
          Add note
        </button>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="space-y-1.5 pt-1.5 border-t border-slate-800/40"
      onClick={stop}
      onMouseDown={stop}
    >
      <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary
                      flex items-center gap-1.5">
        <MessageSquare size={10} />
        Notes · {count}
      </div>

      {notes.map((n) => (
        <NoteRow
          key={n.id}
          note={n}
          currentUserId={currentUserId}
          isServiceProvider={isServiceProvider}
          onEdit={editNote}
          onDelete={deleteNote}
          readOnly={caseComplete}
        />
      ))}

      {/* Compose box at the bottom (ascending order — type after the most
          recent note). Hidden at 'complete' (close lockdown); existing notes
          above stay visible read-only. */}
      {!caseComplete && (
      <div className="space-y-1">
        <textarea
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (inlineError) setInlineError(null); }}
          maxLength={NOTE_SOFT_MAX}
          rows={2}
          disabled={isSubmitting}
          placeholder="Add a note about this finding…"
          className="w-full text-[11px] px-2 py-1.5 rounded border
                     border-slate-700 bg-slate-900 text-slate-100
                     placeholder:text-slate-600
                     focus:outline-none focus:border-slate-500
                     disabled:opacity-50 leading-snug resize-none"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[9px] text-fg-secondary tabular-nums">
            {draft.length}/{NOTE_SOFT_MAX}
          </span>
          <div className="flex items-center gap-1">
            {count === 0 && (
              <button
                type="button"
                onClick={() => { setComposeOpen(false); setDraft(""); setInlineError(null); }}
                disabled={isSubmitting}
                className="text-[10px] px-2 py-0.5 rounded border
                           border-slate-700 text-slate-400 hover:bg-slate-800
                           disabled:opacity-40 transition"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !draft.trim()}
              className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border
                         bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                         hover:bg-[#16f2d4]/25
                         disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {isSubmitting && <Loader2 size={9} className="animate-spin" />}
              {isSubmitting ? "Adding…" : "Add note"}
            </button>
          </div>
        </div>
        {inlineError && (
          <div className="flex items-start gap-1.5 text-[10px] text-red-300 leading-snug">
            <AlertCircle size={10} className="mt-0.5 shrink-0" />
            <span>{inlineError}</span>
          </div>
        )}
      </div>
      )}
    </div>
  );
});

export default FindingNotesSection;
